/**
 * @file 节点操作悬浮菜单组件
 * 替代原来的右键菜单，在选中节点时显示操作按钮
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import type { Core, NodeSingular, Position } from 'cytoscape';
import { MMNode, ChildGroupType } from '../model/node-model';
import { originalEdgeArray } from '../model/edge-model';
import { getInvalidStateAdditionMessage } from '../utils/opl-rules';

// 菜单项类型
export interface MenuItem {
  id: string;
  label: string | ((node: NodeSingular) => string);
  icon?: string;
  disabled?: boolean | ((node: NodeSingular) => boolean);
  divider?: boolean;
  // 显示条件：返回 true 时显示该菜单项
  visible?: (node: NodeSingular) => boolean;
  onClick?: (node: NodeSingular) => void;
}

interface NodeActionMenuProps {
  // cytoscape 实例
  cy: Core | null;
  // 选中的节点
  selectedNode: NodeSingular | null;
  // 菜单项列表
  menuItems: MenuItem[];
  // 菜单关闭回调
  onClose?: () => void;
  // 自定义偏移
  offset?: { x: number; y: number };
}

const NodeActionMenu: React.FC<NodeActionMenuProps> = ({
  cy,
  selectedNode,
  menuItems,
  onClose,
  offset = { x: 10, y: -10 },
}) => {
  const [position, setPosition] = useState<Position | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // 缓存 selectedNode id，避免依赖数组问题
  const selectedNodeId = selectedNode?.id();
  // 缓存 offset 值
  const offsetX = offset.x;
  const offsetY = offset.y;

  // 计算菜单位置（节点右上角）
  const updatePosition = useCallback(() => {
    if (!selectedNode || !cy) {
      setPosition(null);
      return;
    }

    const bb = selectedNode.boundingBox({ includeLabels: false });
    const pan = cy.pan();
    const zoom = cy.zoom();

    // 节点右上角的屏幕坐标
    const screenPos = {
      x: bb.x2 * zoom + pan.x + offsetX,
      y: bb.y1 * zoom + pan.y + offsetY,
    };

    setPosition(screenPos);
  }, [selectedNode, cy, offsetX, offsetY]);

  // 监听节点和画布变化
  useEffect(() => {
    updatePosition();

    if (!selectedNodeId || !cy) return;

    const handleChange = () => updatePosition();

    cy.on('position', `#${selectedNodeId}`, handleChange);
    cy.on('pan zoom', handleChange);

    return () => {
      cy.off('position', `#${selectedNodeId}`, handleChange);
      cy.off('pan zoom', handleChange);
    };
  }, [selectedNodeId, cy, updatePosition]);

  // 过滤可见的菜单项
  const visibleMenuItems = useMemo(() => {
    if (!selectedNode) return [];
    return menuItems
      .filter(item => {
        if (item.visible) {
          return item.visible(selectedNode);
        }
        return true;
      })
      .map(item => ({
        ...item,
        label:
          typeof item.label === 'function'
            ? item.label(selectedNode)
            : item.label,
        disabled:
          typeof item.disabled === 'function'
            ? item.disabled(selectedNode)
            : !!item.disabled,
      }));
  }, [selectedNode, menuItems]);

  // 处理菜单项点击
  const handleItemClick = useCallback(
    (item: MenuItem) => {
      if (item.disabled || !selectedNode) return;

      if (item.onClick) {
        item.onClick(selectedNode);
      }

      setShowDropdown(false);
    },
    [selectedNode]
  );

  // 处理按钮点击
  const handleButtonClick = useCallback(() => {
    setShowDropdown(prev => !prev);
  }, []);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.node-action-menu')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // 当选中节点变化时关闭下拉菜单
  useEffect(() => {
    setShowDropdown(false);
  }, [selectedNodeId]);

  if (!selectedNode || !position) {
    return null;
  }

  return (
    <div
      className="node-action-menu"
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex: 1001,
      }}
    >
      {/* 操作按钮 */}
      <button
        className="node-action-button"
        onClick={handleButtonClick}
        title="节点操作"
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: 'none',
          backgroundColor: showDropdown ? '#0d2847' : '#1a376399',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 'bold',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}
      >
        ⋮
      </button>

      {/* 下拉菜单 */}
      {showDropdown && (
        <div
          className="node-action-dropdown"
          style={{
            position: 'absolute',
            top: 32,
            left: 0,
            minWidth: 150,
            backgroundColor: '#fff',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid #d9d9d9',
            padding: '4px 0',
          }}
        >
          {visibleMenuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <div
                className={`node-action-menu-item ${item.disabled ? 'disabled' : ''}`}
                onClick={() => handleItemClick(item)}
                style={{
                  padding: '8px 12px',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: 'transparent',
                  color: item.disabled ? '#999' : '#333',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={e => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {item.icon && <span>{item.icon}</span>}
                <span>{item.label}</span>
              </div>
              {/* divider: true 表示这个菜单项后面有分隔线 */}
              {item.divider && (
                <div
                  style={{
                    height: 1,
                    backgroundColor: '#e8e8e8',
                    margin: '4px 0',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default NodeActionMenu;

// 默认菜单项配置（基于原右键菜单）
export const createDefaultMenuItems = (
  dispatch: Function,
  currentDiagram: any,
  handlers: {
    onHide: (node: NodeSingular) => void;
    onRemove: (node: NodeSingular) => void;
    onDuplicate: (node: NodeSingular) => void;
    onInZoom: (node: NodeSingular) => void;
    onAddState: (node: NodeSingular) => void;
    onAddObject: (node: NodeSingular) => void;
    onAddProcess: (node: NodeSingular) => void;
    onChangeEssence: (node: NodeSingular) => void;
    onChangeAffiliation: (node: NodeSingular) => void;
    onBringConnected: (node: NodeSingular) => void;
    onBringAllStates: (node: NodeSingular) => void;
    onShowHidden: () => void;
    onImportData: (node: NodeSingular) => void;
    onFunctionDefinition: (node: NodeSingular) => void;
    onDataMap: (node: NodeSingular) => void;
  }
): MenuItem[] => {
  return [
    // ========== 添加状态（仅 Object 节点）==========
    {
      id: 'add-state',
      label: '添加状态',
      // Object 节点：非父节点 或 childGroupType 为 None/State 时显示
      visible: node => {
        const type = node.data('type');
        if (type !== 'object') return false;
        // 非父节点：显示（初始状态）
        if (!node.isParent()) return true;
        // 父节点：只有 None/undefined 或 State 类型才显示
        const childGroupType = node.data('childGroupType') as
          | ChildGroupType
          | undefined;
        return (
          !childGroupType ||
          childGroupType === ChildGroupType.None ||
          childGroupType === ChildGroupType.State
        );
      },
      disabled: node => {
        const mmRef = node.data('MMRef') as MMNode;
        const existingStateCount = (mmRef?.children || []).filter(
          child => child.type === 'state' && !child.deleted
        ).length;
        if (existingStateCount > 0) {
          return false;
        }
        return !!getInvalidStateAdditionMessage(mmRef, originalEdgeArray.edges);
      },
      onClick: handlers.onAddState,
      divider: true,
    },

    // ========== 添加对象/过程（仅 InZoom 父节点）==========
    {
      id: 'add-object',
      label: '添加对象',
      // 父节点且 childGroupType 为 InZoom 时显示
      visible: node => {
        if (!node.isParent()) return false;
        const childGroupType = node.data('childGroupType') as ChildGroupType;
        return childGroupType === ChildGroupType.InZoom;
      },
      onClick: handlers.onAddObject,
    },
    {
      id: 'add-process',
      label: '添加过程',
      // 父节点且 childGroupType 为 InZoom 时显示
      visible: node => {
        if (!node.isParent()) return false;
        const childGroupType = node.data('childGroupType') as ChildGroupType;
        return childGroupType === ChildGroupType.InZoom;
      },
      onClick: handlers.onAddProcess,
      divider: true,
    },

    // ========== 数据映射（对象/过程）==========
    {
      id: 'data-map',
      label: '数据映射',
      onClick: handlers.onDataMap,
      divider: true,
    },

    // ========== 函数定义（仅 Process 节点）==========
    {
      id: 'function-definition',
      label: '函数定义',
      visible: node => {
        const type = node.data('type');
        return type === 'process';
      },
      onClick: handlers.onFunctionDefinition,
      divider: true,
    },

    // ========== 展开子图（仅非父节点的 Object/Process）==========
    {
      id: 'in-zoom',
      label: node => {
        const mmRef = node.data('MMRef') as MMNode | null;
        return mmRef?.diagram && mmRef.diagram !== currentDiagram
          ? '展开子图'
          : '新建子图';
      },
      // Object/Process 节点且非父节点时显示
      visible: node => {
        const type = node.data('type');
        if ((type === 'process' || type === 'object') && !node.isParent()) {
          // 当前图的主节点不允许继续展开子图（与旧版 OPMEditor 行为保持一致）
          const mmRef = node.data('MMRef');
          if (
            mmRef &&
            currentDiagram?.mainNode &&
            mmRef === currentDiagram.mainNode
          ) {
            return false;
          }
          return true;
        }
        return false;
      },
      onClick: handlers.onInZoom,
      divider: true,
    },

    // ========== 复制 ==========
    {
      id: 'duplicate',
      label: '复制',
      // 状态/值节点
      visible: node => {
        const type = node.data('type');
        return type !== 'state' && type !== 'value';
      },
      onClick: handlers.onDuplicate,
    },

    // ========== 删除 ==========
    {
      id: 'remove',
      label: '删除',
      onClick: handlers.onRemove,
    },

    // ========== 以下菜单暂时注释 ==========
    // {
    //   id: 'hide',
    //   label: 'Hide',
    //   onClick: handlers.onHide,
    // },
    // {
    //   id: 'change-essence',
    //   label: 'Change Essence',
    //   visible: node => node.data('type') !== 'state',
    //   onClick: handlers.onChangeEssence,
    // },
    // {
    //   id: 'change-affiliation',
    //   label: 'Change Affiliation',
    //   visible: node => node.data('type') !== 'state',
    //   onClick: handlers.onChangeAffiliation,
    //   divider: true,
    // },
    // {
    //   id: 'bring-connected',
    //   label: 'Bring Connected',
    //   onClick: handlers.onBringConnected,
    // },
    // {
    //   id: 'bring-all-states',
    //   label: 'Bring All States',
    //   visible: node => node.data('type') === 'object',
    //   onClick: handlers.onBringAllStates,
    // },
    // {
    //   id: 'show-hidden',
    //   label: 'Show Hidden',
    //   onClick: handlers.onShowHidden,
    // },
  ];
};
